import { useMemo, useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Input } from '../../../../design-system/input/input';
import { Stack } from '../../../../design-system/stack/stack';
import { BeneficiaryView } from '../../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../../types/supported-country-currency';
import { PayrollAction } from '../../use-payroll-flow';
import { BeneficiaryCard } from './beneficary-card';

type BeneficiariesListProps = {
  beneficiaries: BeneficiaryView[];
  payrollCurrency: SupportedCountryCurrency;
  isLoading: boolean;
  onSetPayrollAction: (action: PayrollAction) => void;
  onManuallyAddEmployees: () => void;
  handleCreateEmployeesFromBeneficiaries: (beneficiaryIds: string[]) => void;
};

export const BeneficiariesList = ({
  beneficiaries,
  payrollCurrency,
  isLoading,
  onSetPayrollAction,
  onManuallyAddEmployees,
  handleCreateEmployeesFromBeneficiaries,
}: BeneficiariesListProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBeneficiaryIds, setSelectedBeneficiaryIds] = useState<string[]>([]);

  const filteredAndGroupedBeneficiaries = useMemo(() => {
    // Filter beneficiaries based on search query
    const filtered = beneficiaries.filter((beneficiary) =>
      beneficiary.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    // Group beneficiaries by first letter
    return filtered.reduce<Record<string, BeneficiaryView[]>>((groups, beneficiary) => {
      const firstLetter = beneficiary.name.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(beneficiary);
      return groups;
    }, {});
  }, [beneficiaries, searchQuery]);

  const hasFilteredResults = Object.keys(filteredAndGroupedBeneficiaries).length > 0;

  const handleBeneficiaryToggle = (beneficiaryId: string) => {
    setSelectedBeneficiaryIds((prev) => {
      const isSelected = prev.includes(beneficiaryId);
      if (isSelected) {
        return prev.filter((id) => id !== beneficiaryId);
      } else {
        return [...prev, beneficiaryId];
      }
    });
  };

  return (
    <Stack space={6} className="w-full">
      <div className="w-full">
        <Input
          className="w-full border-none bg-white p-4"
          type="text"
          placeholder="Search employees"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <Stack space={6} className="w-full">
        {beneficiaries.length === 0 ? (
          <div className="text-sm text-gray-500">No beneficiaries, you'll have to manually add employees</div>
        ) : !hasFilteredResults && searchQuery ? (
          <div className="text-sm text-gray-500">No results for "{searchQuery}"</div>
        ) : (
          Object.entries(filteredAndGroupedBeneficiaries)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([letter, groupBeneficiaries]) => (
              <Stack key={letter} space={4} className="w-full">
                <div className="text-sm font-medium text-gray-500">{letter}</div>
                <Stack space={3} className="w-full">
                  {groupBeneficiaries.map((beneficiary) => (
                    <BeneficiaryCard
                      key={beneficiary.id}
                      isSelected={selectedBeneficiaryIds.includes(beneficiary.id)}
                      beneficiaryDetails={{
                        id: beneficiary.id,
                        name: beneficiary.name,
                        destinationConfig: {
                          beneficiaryType: beneficiary.destination.type,
                          iban: beneficiary.destination.iban ?? '',
                          accountNumber: beneficiary.destination.accountNumber ?? '',
                          sortCode: beneficiary.destination.sortCode ?? '',
                        },
                      }}
                      payrollCurrency={payrollCurrency}
                      onSelect={() => handleBeneficiaryToggle(beneficiary.id)}
                    />
                  ))}
                </Stack>
              </Stack>
            ))
        )}
      </Stack>
      <Inline className="w-full" space={2} justify="between" wrap="nowrap">
        <Button
          className="w-full"
          onClick={async () => {
            await handleCreateEmployeesFromBeneficiaries(selectedBeneficiaryIds);
            onSetPayrollAction('MANUAL_ADD_EMPLOYEES');
          }}
          disabled={selectedBeneficiaryIds.length === 0 || isLoading}
          isLoading={isLoading}
        >
          {isLoading ? 'Creating employees...' : 'Continue'}
        </Button>
        <Button
          variant="secondary"
          className="w-full border-none bg-white hover:bg-accent"
          onClick={onManuallyAddEmployees}
          disabled={selectedBeneficiaryIds.length !== 0 || isLoading}
        >
          I'll add manually
        </Button>
      </Inline>
    </Stack>
  );
};
